import { authService } from './authService';
import { isDemoMode } from '../config/authConfig';
import { apiCache, CACHE_CONFIG } from '../utils/apiCache';
import { logger } from '../utils/logger';
import { getAppOnlyToken } from './convexService';

// Mock data for demo mode
const MOCK_USERS = [
  {
    id: '1',
    displayName: 'John Doe',
    userPrincipalName: 'john.doe@demo.com',
    mail: 'john.doe@demo.com',
    jobTitle: 'Software Engineer',
    department: 'Engineering',
    accountEnabled: true,
    createdDateTime: '2024-01-15T00:00:00Z',
    lastPasswordChangeDateTime: '2024-10-01T00:00:00Z',
    officeLocation: 'Building 1',
    companyName: 'Demo Company',
    mobilePhone: '+1 555-0101',
    businessPhones: ['+1 555-0100'],
  },
  {
    id: '2',
    displayName: 'Jane Smith',
    userPrincipalName: 'jane.smith@demo.com',
    mail: 'jane.smith@demo.com',
    jobTitle: 'Product Manager',
    department: 'Product',
    accountEnabled: true,
    createdDateTime: '2024-02-20T00:00:00Z',
    lastPasswordChangeDateTime: '2024-09-15T00:00:00Z',
    officeLocation: 'Building 2',
    companyName: 'Demo Company',
    mobilePhone: '+1 555-0102',
    businessPhones: ['+1 555-0103'],
  },
  {
    id: '3',
    displayName: 'Bob Johnson',
    userPrincipalName: 'bob.johnson@demo.com',
    mail: 'bob.johnson@demo.com',
    jobTitle: 'Designer',
    department: 'Design',
    accountEnabled: false,
    createdDateTime: '2024-03-10T00:00:00Z',
    lastPasswordChangeDateTime: '2024-08-20T00:00:00Z',
    officeLocation: 'Building 1',
    companyName: 'Demo Company',
    mobilePhone: '+1 555-0104',
    businessPhones: ['+1 555-0105'],
  },
  {
    id: '4',
    displayName: 'Alice Brown',
    userPrincipalName: 'alice.brown@demo.com',
    mail: 'alice.brown@demo.com',
    jobTitle: 'Marketing Manager',
    department: 'Marketing',
    accountEnabled: true,
    createdDateTime: '2024-04-05T00:00:00Z',
    lastPasswordChangeDateTime: '2024-10-10T00:00:00Z',
    officeLocation: 'Building 2',
    companyName: 'Demo Company',
    mobilePhone: '+1 555-0106',
    businessPhones: ['+1 555-0107'],
  },
];

const MOCK_DEVICES = [
  {
    id: 'device-1',
    deviceName: 'LAPTOP-001',
    operatingSystem: 'Windows 11',
    osVersion: '10.0.22000.1',
    complianceState: 'compliant',
    lastSyncDateTime: new Date().toISOString(),
    userPrincipalName: 'john.doe@demo.com',
  },
  {
    id: 'device-2',
    deviceName: 'LAPTOP-002',
    operatingSystem: 'macOS',
    osVersion: '13.0',
    complianceState: 'compliant',
    lastSyncDateTime: new Date().toISOString(),
    userPrincipalName: 'jane.smith@demo.com',
  },
];

export class GraphService {
  constructor() {
    this.baseUrl = 'https://graph.microsoft.com/v1.0';
    this.betaUrl = 'https://graph.microsoft.com/beta';
    this.maxRetries = 3;
  }

  /**
   * Stub method for compatibility with msalGraphService interface
   * graphService uses Convex app-only tokens, so this is a no-op
   */
  setGetTokenFunction(fn) {
    // No-op: graphService uses Convex app-only tokens, not MSAL delegated tokens
    console.log('⚠️ graphService.setGetTokenFunction called - this is a no-op for app-only auth');
  }

  /**
   * Categorize and handle Graph API errors with actionable information
   * @param {Error} error - The error object
   * @param {string} operation - Description of the operation that failed
   * @returns {Object} Structured error information
   */
  handleGraphError(error, operation = 'operation') {
    const errorMessage = error.message || '';
    
    if (errorMessage.includes('429')) {
      return { 
        type: 'throttling', 
        message: 'Too many requests. The request will be retried automatically.',
        retryable: true,
        statusCode: 429
      };
    }
    if (errorMessage.includes('401')) {
      return { 
        type: 'authentication', 
        message: 'Authentication failed. Please sign in again.',
        retryable: false,
        statusCode: 401
      };
    }
    if (errorMessage.includes('403')) {
      return { 
        type: 'permission', 
        message: 'You do not have permission to perform this operation. Please contact your administrator.',
        retryable: false,
        statusCode: 403
      };
    }
    if (errorMessage.includes('404')) {
      return { 
        type: 'not_found', 
        message: 'The requested resource was not found.',
        retryable: false,
        statusCode: 404
      };
    }
    if (errorMessage.includes('500') || errorMessage.includes('503')) {
      return { 
        type: 'server_error', 
        message: 'The server is experiencing issues. Please try again in a moment.',
        retryable: true,
        statusCode: parseInt(errorMessage.match(/\d{3}/)?.[0]) || 500
      };
    }
    if (errorMessage.includes('400')) {
      return { 
        type: 'bad_request', 
        message: 'Invalid request. Please check your input and try again.',
        retryable: false,
        statusCode: 400
      };
    }
    
    return { 
      type: 'unknown', 
      message: `Failed to ${operation}. Please try again.`,
      retryable: true,
      statusCode: 0,
      originalError: errorMessage
    };
  }

  /**
   * Generic method to make Graph API calls with retry logic and throttling handling
   * @param {string} endpoint - The API endpoint (e.g., '/users')
   * @param {Object} options - Fetch options
   * @param {number} retryCount - Current retry attempt (internal use)
   * @returns {Promise} API response
   */
  async makeRequest(endpoint, options = {}, retryCount = 0) {
    // Return mock data in demo mode
    if (isDemoMode()) {
      return this._getMockData(endpoint, options);
    }

    try {
      const authMode = localStorage.getItem('authMode');
      const useAppOnly = authMode === 'app-only';

      let token;
      try {
        if (useAppOnly) {
          token = await getAppOnlyToken();
          logger.debug('🔑 Using app-only token for Graph API');
        } else {
          token = await authService.getAccessToken();
          logger.debug('🔑 Using SSO token for Graph API');
        }
      } catch (tokenError) {
        if (useAppOnly) {
          logger.error('❌ App-only token acquisition failed');
        }
        throw tokenError;
      }
      
      // Ensure endpoint starts with / for proper URL construction
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const url = `${this.baseUrl}${cleanEndpoint}`;
      
      const defaultOptions = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      // Merge headers properly to avoid overwriting Authorization header
      const mergedOptions = {
        ...defaultOptions,
        ...options,
        headers: {
          ...defaultOptions.headers,
          ...(options.headers || {})
        }
      };

      const response = await fetch(url, mergedOptions);
      
      // Handle throttling (429 Too Many Requests)
      if (response.status === 429 && retryCount < this.maxRetries) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '5');
        logger.warn(`Request throttled. Retrying after ${retryAfter} seconds... (Attempt ${retryCount + 1}/${this.maxRetries})`);
        
        // Wait for the specified retry period
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        
        // Retry the request
        return this.makeRequest(endpoint, options, retryCount + 1);
      }

      // Handle server errors with exponential backoff
      if ((response.status === 500 || response.status === 503) && retryCount < this.maxRetries) {
        const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Max 10 seconds
        logger.warn(`Server error (${response.status}). Retrying after ${backoffDelay}ms... (Attempt ${retryCount + 1}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return this.makeRequest(endpoint, options, retryCount + 1);
      }
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: { message: response.statusText } };
        }
        const error = new Error(`Graph API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
        error.statusCode = response.status;
        error.errorData = errorData;
        throw error;
      }
      
      // Handle empty responses (204 No Content)
      if (response.status === 204) {
        return { success: true };
      }
      
      // Check if response has content before parsing JSON
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      // If no content or not JSON, return empty result
      if (contentLength === '0' || !contentType?.includes('application/json')) {
        // Try to get text to see if there's any content
        const text = await response.text();
        if (!text || text.trim() === '') {
          return { value: [] }; // Return empty array for list endpoints
        }
        // If there is text, try to parse it as JSON
        try {
          return JSON.parse(text);
        } catch (e) {
          logger.warn('Response is not JSON:', text.substring(0, 100));
          return { value: [] };
        }
      }
      
      return await response.json();
    } catch (error) {
      const structuredError = this.handleGraphError(error, 'complete the request');
      logger.error('Graph API request error:', {
        endpoint,
        error: structuredError,
        retryCount,
        originalError: error
      });
      
      // Attach structured error info to the error object
      error.graphError = structuredError;
      throw error;
    }
  }

  /**
   * Make a request to the Microsoft Graph BETA API
   * Use this only when the v1.0 endpoint is not available
   * @param {string} endpoint - The API endpoint (e.g., '/security/vulnerabilities')
   * @param {Object} options - Fetch options
   * @param {number} retryCount - Current retry attempt (internal use)
   * @returns {Promise} API response
   */
  async makeBetaRequest(endpoint, options = {}, retryCount = 0) {
    // Return mock data in demo mode
    if (isDemoMode()) {
      return this._getMockData(endpoint, options);
    }

    try {
      const authMode = localStorage.getItem('authMode');
      const useAppOnly = authMode === 'app-only';
      const token = useAppOnly 
        ? await getAppOnlyToken()
        : await authService.getAccessToken();
      const url = `${this.betaUrl}${endpoint}`;
      
      const defaultOptions = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      // Merge headers properly to avoid overwriting Authorization header
      const mergedOptions = {
        ...defaultOptions,
        ...options,
        headers: {
          ...defaultOptions.headers,
          ...(options.headers || {})
        }
      };

      const response = await fetch(url, mergedOptions);
      
      // Handle throttling (429 Too Many Requests)
      if (response.status === 429 && retryCount < this.maxRetries) {
        const retryAfter = parseInt(response.headers.get('Retry-After') || '5');
        logger.warn(`Rate limited. Retrying after ${retryAfter} seconds... (Attempt ${retryCount + 1}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        return this.makeBetaRequest(endpoint, options, retryCount + 1);
      }
      
      // Handle server errors with exponential backoff
      if ((response.status === 500 || response.status === 503) && retryCount < this.maxRetries) {
        const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Max 10 seconds
        logger.warn(`Server error (${response.status}). Retrying after ${backoffDelay}ms... (Attempt ${retryCount + 1}/${this.maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return this.makeBetaRequest(endpoint, options, retryCount + 1);
      }
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: { message: response.statusText } };
        }
        const error = new Error(`Graph API (beta) error: ${response.status} - ${errorData.error?.message || response.statusText}`);
        error.statusCode = response.status;
        error.errorData = errorData;
        throw error;
      }
      
      // Handle empty responses (204 No Content)
      if (response.status === 204) {
        return { success: true };
      }
      
      // Check if response has content before parsing JSON
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type');
      
      // If no content or not JSON, return empty result
      if (contentLength === '0' || !contentType?.includes('application/json')) {
        const text = await response.text();
        if (!text || text.trim() === '') {
          return { value: [] };
        }
        try {
          return JSON.parse(text);
        } catch (e) {
          logger.warn('Beta response is not JSON:', text.substring(0, 100));
          return { value: [] };
        }
      }
      
      return await response.json();
    } catch (error) {
      const structuredError = this.handleGraphError(error, 'complete the beta request');
      logger.error('Graph API (beta) request error:', {
        endpoint,
        error: structuredError,
        retryCount,
        originalError: error
      });
      
      // Attach structured error info to the error object
      error.graphError = structuredError;
      throw error;
    }
  }

  // Mock data handler for demo mode
  _getMockData(endpoint, options = {}) {
    logger.debug('Demo mode: returning mock data for', endpoint);
    
    // Simulate async operation
    return new Promise((resolve) => {
      setTimeout(() => {
        // Handle different endpoints
        if (endpoint.includes('/users')) {
          if (endpoint.includes('/users/') && !endpoint.includes('/memberOf')) {
            // Single user request
            const userId = endpoint.split('/users/')[1].split('?')[0];
            const user = MOCK_USERS.find(u => u.id === userId) || MOCK_USERS[0];
            resolve(user);
          } else {
            // User list request
            resolve({ value: MOCK_USERS });
          }
        } else if (endpoint.includes('/deviceManagement/managedDevices')) {
          resolve({ value: MOCK_DEVICES });
        } else if (endpoint.includes('/memberOf')) {
          // Mock groups
          resolve({ value: [] });
        } else if (endpoint.includes('/mailboxSettings')) {
          resolve({
            automaticRepliesSetting: {
              status: 'disabled',
            },
          });
        } else {
          // Default empty response
          resolve({ value: [] });
        }
      }, 300); // Simulate network delay
    });
  }

  // User Management Methods
  
  /**
   * Get users with pagination support
   * Note: Microsoft Graph API doesn't support $skip for users endpoint
   * Use $top for page size and @odata.nextLink for pagination
   * @param {number} top - Number of users per page
   * @param {string} filter - OData filter string
   * @param {string} nextLink - Optional next page URL from previous response
   * @returns {Promise} Response with value array and optional @odata.nextLink
   */
  async getUsers(top = 25, filter = '', nextLink = null) {
    let endpoint;
    
    if (nextLink) {
      // Use the nextLink URL directly (remove base URL if present)
      endpoint = nextLink.replace(this.baseUrl, '');
    } else {
      // Build initial query
      endpoint = `/users?$top=${top}&$select=id,displayName,userPrincipalName,mail,jobTitle,department,accountEnabled,createdDateTime,lastPasswordChangeDateTime`;
      
      if (filter) {
        endpoint += `&$filter=${filter}`;
      }
    }
    
    return this.makeRequest(endpoint);
  }

  /**
   * Get all users by following @odata.nextLink pagination
   * @param {string} filter - Optional OData filter string
   * @param {number} pageSize - Number of users per page (default: 100)
   * @returns {Promise<Array>} All users matching the filter
   */
  async getAllUsers(filter = '', pageSize = 100) {
    // Check cache first
    const cacheKey = apiCache.generateKey('/users/all', { filter, pageSize });
    const cached = apiCache.get(cacheKey);
    
    if (cached) {
      logger.debug('📦 Returning cached users:', cached.totalCount);
      return cached;
    }
    
    logger.debug('🌐 Fetching users from API...');
    const users = [];
    let endpoint = `/users?$top=${pageSize}&$select=id,displayName,userPrincipalName,mail,jobTitle,department,accountEnabled,createdDateTime,lastPasswordChangeDateTime`;
    
    if (filter) {
      endpoint += `&$filter=${filter}`;
    }
    
    // Follow pagination links
    while (endpoint) {
      const response = await this.makeRequest(endpoint.replace(this.baseUrl, ''));
      
      if (response.value) {
        users.push(...response.value);
      }
      
      // Check for next page
      endpoint = response['@odata.nextLink'] || null;
      
      // Log progress for large datasets
      if (endpoint) {
        logger.debug(`Fetched ${users.length} users, loading more...`);
      }
    }
    
    const result = { value: users, totalCount: users.length };
    
    // Cache the result
    apiCache.set(cacheKey, result, CACHE_CONFIG.USERS_LIST.ttl);
    logger.success('✅ Users fetched and cached:', users.length);
    
    return result;
  }

  /**
   * Get paginated users with explicit nextLink handling
   * @param {string} nextLink - The @odata.nextLink from previous response, or null for first page
   * @param {number} pageSize - Number of users per page
   * @param {string} filter - Optional OData filter string
   * @returns {Promise} Response with value array and @odata.nextLink
   */
  async getUsersPage(nextLink = null, pageSize = 25, filter = '') {
    if (nextLink) {
      // Use the full nextLink URL
      return this.makeRequest(nextLink.replace(this.baseUrl, ''));
    } else {
      // First page
      let endpoint = `/users?$top=${pageSize}&$select=id,displayName,userPrincipalName,mail,jobTitle,department,accountEnabled,createdDateTime,lastPasswordChangeDateTime`;
      
      if (filter) {
        endpoint += `&$filter=${filter}`;
      }
      
      return this.makeRequest(endpoint);
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId) {
    return this.makeRequest(`/users/${userId}?$select=id,displayName,userPrincipalName,mail,jobTitle,department,accountEnabled,createdDateTime,lastPasswordChangeDateTime,officeLocation,companyName,mobilePhone,businessPhones`);
  }

  /**
   * Get user changes using delta query for efficient synchronization
   * Use this to track changes instead of repeatedly fetching all users
   * @param {string} deltaLink - The @odata.deltaLink from previous response, or null for initial sync
   * @returns {Promise} Response with changes and new deltaLink
   */
  async getUsersDelta(deltaLink = null) {
    if (isDemoMode()) {
      return { 
        value: MOCK_USERS, 
        '@odata.deltaLink': '/users/delta?$deltatoken=demo-delta-token'
      };
    }

    const endpoint = deltaLink 
      ? deltaLink.replace(this.baseUrl, '')
      : '/users/delta?$select=id,displayName,userPrincipalName,mail,jobTitle,department,accountEnabled,createdDateTime,lastPasswordChangeDateTime';
    
    return this.makeRequest(endpoint);
  }

  /**
   * Execute multiple Graph API requests in a single batch
   * Combines up to 20 requests into one HTTP call for better performance
   * @param {Array} requests - Array of request objects with id, method, url, headers, body
   * @returns {Promise} Response with array of individual responses
   * 
   * Example request format:
   * {
   *   id: '1',
   *   method: 'PATCH',
   *   url: '/users/userId',
   *   headers: { 'Content-Type': 'application/json' },
   *   body: { jobTitle: 'Manager' }
   * }
   */
  async batchRequest(requests) {
    if (isDemoMode()) {
      logger.debug('Demo mode: simulating batch request with', requests.length, 'operations');
      // Simulate successful batch responses in demo mode
      return { 
        responses: requests.map((req, i) => ({ 
          id: req.id, 
          status: 200,
          headers: { 'Content-Type': 'application/json' },
          body: { success: true, id: req.id }
        }))
      };
    }

    // Validate batch size (Microsoft Graph limit is 20)
    if (requests.length > 20) {
      logger.warn(`Batch request has ${requests.length} requests, but limit is 20. Splitting into multiple batches...`);
      
      // Split into chunks of 20
      const batches = [];
      for (let i = 0; i < requests.length; i += 20) {
        batches.push(requests.slice(i, i + 20));
      }
      
      // Execute batches sequentially
      const allResponses = [];
      for (const batch of batches) {
        const response = await this.batchRequest(batch);
        allResponses.push(...response.responses);
      }
      
      return { responses: allResponses };
    }

    const batchBody = {
      requests: requests.map(req => ({
        id: req.id.toString(),
        method: req.method || 'GET',
        url: (req.url || '').replace(this.baseUrl, '').replace('https://graph.microsoft.com/v1.0', ''),
        headers: req.headers || {},
        body: req.body || undefined
      }))
    };

    const response = await this.makeRequest('/$batch', {
      method: 'POST',
      body: JSON.stringify(batchBody)
    });

    // Check for failed requests and log warnings
    const failedRequests = response.responses?.filter(r => r.status >= 400) || [];
    if (failedRequests.length > 0) {
      logger.warn(`Batch request completed with ${failedRequests.length} failed operations:`, 
        failedRequests.map(r => ({ id: r.id, status: r.status, error: r.body?.error?.message }))
      );
    }

    return response;
  }

  async searchUsers(searchTerm) {
    const filter = `startswith(displayName,'${searchTerm}') or startswith(userPrincipalName,'${searchTerm}') or startswith(mail,'${searchTerm}')`;
    return this.makeRequest(`/users?$filter=${filter}&$top=25&$select=id,displayName,userPrincipalName,mail,jobTitle,department`);
  }

  async updateUser(userId, updateData) {
    const result = await this.makeRequest(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
    
    // Invalidate user cache after update
    apiCache.invalidatePattern(/\/users/);
    logger.debug('♻️ Cache invalidated after user update');
    
    return result;
  }

  async disableUser(userId) {
    return this.updateUser(userId, { accountEnabled: false });
  }

  async enableUser(userId) {
    return this.updateUser(userId, { accountEnabled: true });
  }

  /**
   * Reset user password (for admin-initiated password reset during offboarding)
   * Uses PATCH /users/{id} with passwordProfile per Microsoft Graph API best practices
   * @param {string} userId - User ID
   * @param {string} newPassword - New password
   * @param {boolean} forceChangePasswordNextSignIn - Force password change on next sign-in
   * @returns {Promise} Response
   */
  async resetUserPassword(userId, newPassword, forceChangePasswordNextSignIn = false) {
    // In demo mode, just return success
    if (isDemoMode()) {
      return Promise.resolve({ success: true });
    }

    return this.makeRequest(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        passwordProfile: {
          password: newPassword,
          forceChangePasswordNextSignIn: forceChangePasswordNextSignIn,
        },
      }),
    });
  }

  async setUserPassword(userId, newPassword, forceChangePasswordNextSignIn = true) {
    // Alias for resetUserPassword with default forceChange = true
    return this.resetUserPassword(userId, newPassword, forceChangePasswordNextSignIn);
  }

  async sendWelcomeEmail(userId, welcomeMessage, managerEmail) {
    // In demo mode, just return success
    if (isDemoMode()) {
      return Promise.resolve({ success: true });
    }

    // Get user details for the email
    const user = await this.getUserById(userId);
    
    const emailBody = {
      message: {
        subject: 'Welcome to the Team!',
        body: {
          contentType: 'HTML',
          content: `
            <html>
              <body>
                <h2>Welcome ${user.displayName}!</h2>
                <p>${welcomeMessage}</p>
                <p>Your account has been set up and is ready to use.</p>
                <p>If you have any questions, please don't hesitate to reach out${managerEmail ? ` to your manager at ${managerEmail}` : ''}.</p>
                <br/>
                <p>Best regards,<br/>The Team</p>
              </body>
            </html>
          `,
        },
        toRecipients: [
          {
            emailAddress: {
              address: user.mail || user.userPrincipalName,
            },
          },
        ],
      },
    };

    return this.makeRequest(`/users/${userId}/sendMail`, {
      method: 'POST',
      body: JSON.stringify(emailBody),
    });
  }

  async deleteUser(userId) {
    const result = await this.makeRequest(`/users/${userId}`, {
      method: 'DELETE',
    });
    
    // Invalidate user cache after deletion
    apiCache.invalidatePattern(/\/users/);
    logger.debug('🗑️ Cache invalidated after user deletion');
    
    return result;
  }

  async updateUserManager(userId, managerEmail) {
    // In demo mode, just return success
    if (isDemoMode()) {
      return Promise.resolve({ success: true });
    }

    // First, search for the manager by email
    const managerResults = await this.searchUsers(managerEmail);
    if (!managerResults.value || managerResults.value.length === 0) {
      throw new Error('Manager not found');
    }

    const managerId = managerResults.value[0].id;

    return this.makeRequest(`/users/${userId}/manager/$ref`, {
      method: 'PUT',
      body: JSON.stringify({
        "@odata.id": `https://graph.microsoft.com/v1.0/users/${managerId}`
      }),
    });
  }

  async sendTransferNotification(userId, transferOptions, recipientType) {
    // In demo mode, just return success
    if (isDemoMode()) {
      return Promise.resolve({ success: true });
    }

    const user = await this.getUserById(userId);
    
    let subject, content;
    if (recipientType === 'user') {
      subject = 'Your Role Transfer/Promotion';
      content = `
        <html>
          <body>
            <h2>Congratulations ${user.displayName}!</h2>
            <p>We're excited to inform you about your upcoming role change.</p>
            <h3>New Position Details:</h3>
            <ul>
              ${transferOptions.newDepartment ? `<li><strong>Department:</strong> ${transferOptions.newDepartment}</li>` : ''}
              ${transferOptions.newJobTitle ? `<li><strong>Job Title:</strong> ${transferOptions.newJobTitle}</li>` : ''}
              ${transferOptions.newOfficeLocation ? `<li><strong>Location:</strong> ${transferOptions.newOfficeLocation}</li>` : ''}
              ${transferOptions.effectiveDate ? `<li><strong>Effective Date:</strong> ${new Date(transferOptions.effectiveDate).toLocaleDateString()}</li>` : ''}
            </ul>
            ${transferOptions.transferNotes ? `<p><strong>Notes:</strong> ${transferOptions.transferNotes}</p>` : ''}
            <p>If you have any questions, please reach out to your manager or HR.</p>
            <br/>
            <p>Best regards,<br/>Human Resources</p>
          </body>
        </html>
      `;
    } else {
      subject = 'New Team Member Transfer Notification';
      content = `
        <html>
          <body>
            <h2>New Team Member</h2>
            <p>${user.displayName} will be joining your team.</p>
            <h3>Transfer Details:</h3>
            <ul>
              ${transferOptions.newDepartment ? `<li><strong>Department:</strong> ${transferOptions.newDepartment}</li>` : ''}
              ${transferOptions.newJobTitle ? `<li><strong>Job Title:</strong> ${transferOptions.newJobTitle}</li>` : ''}
              ${transferOptions.effectiveDate ? `<li><strong>Start Date:</strong> ${new Date(transferOptions.effectiveDate).toLocaleDateString()}</li>` : ''}
            </ul>
            ${transferOptions.transferNotes ? `<p><strong>Notes:</strong> ${transferOptions.transferNotes}</p>` : ''}
            <p>Please ensure ${user.displayName} has the necessary access and resources.</p>
            <br/>
            <p>Best regards,<br/>Human Resources</p>
          </body>
        </html>
      `;
    }

    const emailBody = {
      message: {
        subject: subject,
        body: {
          contentType: 'HTML',
          content: content,
        },
        toRecipients: [
          {
            emailAddress: {
              address: user.mail || user.userPrincipalName,
            },
          },
        ],
      },
    };

    return this.makeRequest(`/users/${userId}/sendMail`, {
      method: 'POST',
      body: JSON.stringify(emailBody),
    });
  }

  // Group Management Methods
  
  /**
   * Get a specific group by ID
   * @param {string} groupId - The group ID
   * @returns {Promise} The group object
   */
  async getGroup(groupId) {
    return this.makeRequest(`/groups/${groupId}?$select=id,displayName,description,groupTypes,mail,mailNickname,mailEnabled,securityEnabled,visibility,createdDateTime`);
  }

  /**
   * Create a new group
   * @param {Object} groupData - The group data
   * @returns {Promise} The created group
   */
  async createGroup(groupData) {
    return this.makeRequest('/groups', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  /**
   * Update a group
   * @param {string} groupId - The group ID
   * @param {Object} updates - The fields to update
   * @returns {Promise} The update response
   */
  async updateGroup(groupId, updates) {
    return this.makeRequest(`/groups/${groupId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Delete a group
   * @param {string} groupId - The group ID
   * @returns {Promise} The delete response
   */
  async deleteGroup(groupId) {
    return this.makeRequest(`/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get all groups in the organization
   * @param {number} top - Number of groups to return (default: 999)
   * @returns {Promise} Response with groups
   */
  async getGroups(top = 999) {
    return this.makeRequest(`/groups?$top=${top}&$select=id,displayName,description,groupTypes,mail,mailEnabled,securityEnabled`);
  }

  /**
   * Get all groups by following @odata.nextLink pagination
   * @returns {Promise<Array>} All groups
   */
  async getAllGroups() {
    const groups = [];
    let endpoint = `/groups?$top=999&$select=id,displayName,description,groupTypes,mail,mailEnabled,securityEnabled`;
    
    // Follow pagination links
    while (endpoint) {
      const response = await this.makeRequest(endpoint.replace(this.baseUrl, ''));
      
      if (response.value) {
        groups.push(...response.value);
      }
      
      // Check for next page
      endpoint = response['@odata.nextLink'] || null;
      
      if (endpoint) {
        logger.debug(`Fetched ${groups.length} groups, loading more...`);
      }
    }
    
    logger.debug(`Total groups fetched: ${groups.length}`);
    return { value: groups, totalCount: groups.length };
  }
  
  async getUserGroups(userId) {
    try {
      const response = await this.makeRequest(`/users/${userId}/memberOf?$select=id,displayName,groupTypes,membershipRule`);
      // Filter to only non-dynamic groups (dynamic groups cannot be managed via Graph API)
      // Dynamic groups have "DynamicMembership" in groupTypes array
      const groups = (response.value || []).filter(item => {
        if (!item['@odata.type'] || !item['@odata.type'].includes('group')) {
          return false;
        }
        // Exclude dynamic groups (groupTypes contains "DynamicMembership")
        const groupTypes = item.groupTypes || [];
        return !groupTypes.includes('DynamicMembership');
      });
      return {
        value: groups,
        total: groups.length
      };
    } catch (error) {
      logger.error('Error fetching user groups:', error);
      throw error;
    }
  }

  async addUserToGroup(groupId, userId) {
    return this.makeRequest(`/groups/${groupId}/members/$ref`, {
      method: 'POST',
      body: JSON.stringify({
        "@odata.id": `https://graph.microsoft.com/v1.0/users/${userId}`
      }),
    });
  }

  async removeUserFromGroup(groupId, userId) {
    return this.makeRequest(`/groups/${groupId}/members/${userId}/$ref`, {
      method: 'DELETE',
    });
  }

  /**
   * Get group members
   * @param {string} groupId - The group ID
   * @returns {Promise<Array>} Array of group members
   */
  async getGroupMembers(groupId) {
    const response = await this.makeRequest(`/groups/${groupId}/members?$select=id,displayName,mail,userPrincipalName`);
    return response.value || [];
  }

  /**
   * Get group owners
   * @param {string} groupId - The group ID
   * @returns {Promise<Array>} Array of group owners
   */
  async getGroupOwners(groupId) {
    const response = await this.makeRequest(`/groups/${groupId}/owners?$select=id,displayName,mail,userPrincipalName`);
    return response.value || [];
  }

  /**
   * Add a user as group owner
   * @param {string} groupId - The group ID
   * @param {string} userId - The user ID to add as owner
   */
  async addGroupOwner(groupId, userId) {
    return this.makeRequest(`/groups/${groupId}/owners/$ref`, {
      method: 'POST',
      body: JSON.stringify({
        "@odata.id": `https://graph.microsoft.com/v1.0/users/${userId}`
      }),
    });
  }

  /**
   * Remove a user as group owner
   * @param {string} groupId - The group ID
   * @param {string} userId - The user ID to remove as owner
   */
  async removeGroupOwner(groupId, userId) {
    return this.makeRequest(`/groups/${groupId}/owners/${userId}/$ref`, {
      method: 'DELETE',
    });
  }

  /**
   * Get user email activity (last 30 days)
   * Requires Reports.Read.All permission
   * @param {string} userId - User ID or UPN
   * @returns {Promise<Object>} Email activity data
   */
  async getUserEmailActivity(userId) {
    try {
      // Get email activity for all users over the last 30 days
      const response = await this.makeRequest(
        `/reports/getEmailActivityUserDetail(period='D30')?$format=application/json`
      );
      
      // Find the specific user's activity
      const userActivity = (response.value || []).find(
        activity => activity.userPrincipalName?.toLowerCase() === userId?.toLowerCase() ||
                   activity.userId === userId
      );
      
      if (userActivity) {
        return {
          lastActivityDate: userActivity.lastActivityDate,
          sendCount: userActivity.sendCount || 0,
          receiveCount: userActivity.receiveCount || 0,
          readCount: userActivity.readCount || 0,
          meetingCreatedCount: userActivity.meetingCreatedCount || 0,
          meetingInteractedCount: userActivity.meetingInteractedCount || 0,
        };
      }
      
      return null;
    } catch (error) {
      // Reports API might not be available, try alternative approach
      console.warn('Reports API not available, trying alternative method:', error.message);
      
      try {
        // Alternative: Get last message received in mailbox
        const messages = await this.makeRequest(
          `/users/${userId}/messages?$top=1&$orderby=receivedDateTime desc&$select=receivedDateTime,from`
        );
        
        const lastReceived = messages.value?.[0]?.receivedDateTime;
        
        // Get sent items
        const sentMessages = await this.makeRequest(
          `/users/${userId}/mailFolders/sentItems/messages?$top=1&$orderby=sentDateTime desc&$select=sentDateTime`
        );
        
        const lastSent = sentMessages.value?.[0]?.sentDateTime;
        
        // Determine last activity date
        let lastActivityDate = null;
        if (lastReceived && lastSent) {
          lastActivityDate = new Date(lastReceived) > new Date(lastSent) ? lastReceived : lastSent;
        } else {
          lastActivityDate = lastReceived || lastSent;
        }
        
        return {
          lastActivityDate,
          sendCount: sentMessages.value?.length || 0,
          receiveCount: messages.value?.length || 0,
          readCount: 0,
        };
      } catch (altError) {
        console.warn('Alternative method also failed:', altError.message);
        return null;
      }
    }
  }

  // Mail Management Methods
  async getUserMailboxSettings(userId) {
    return this.makeRequest(`/users/${userId}/mailboxSettings`);
  }

  async updateUserMailboxSettings(userId, settings) {
    return this.makeRequest(`/users/${userId}/mailboxSettings`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async setAutoReply(userId, isEnabled, externalAudience, internalReply, externalReply) {
    return this.updateUserMailboxSettings(userId, {
      automaticRepliesSetting: {
        status: isEnabled ? 'AlwaysEnabled' : 'Disabled',
        externalAudience: externalAudience || 'All',
        internalReply: internalReply || '',
        externalReply: externalReply || '',
      },
    });
  }

  async setMailForwarding(userId, forwardingAddress, deliverToMailboxAndForward) {
    return this.updateUserMailboxSettings(userId, {
      forwardingAddress: forwardingAddress,
      deliverToMailboxAndForwardingAddress: deliverToMailboxAndForward || false,
    });
  }

  /**
   * Convert to shared mailbox
   * NOTE: This operation is NOT supported by Microsoft Graph API
   * It requires Exchange Online PowerShell: Set-Mailbox -Identity {email} -Type Shared
   * This method returns an error with instructions for the administrator
   * @param {string} userId - User ID
   * @returns {Promise} Error response with instructions
   */
  async convertToSharedMailbox(userId) {
    // Get user email for reference
    try {
      const user = await this.makeRequest(`/users/${userId}?$select=id,userPrincipalName,mail`);
      const email = user.mail || user.userPrincipalName;
      
      // Return error with instructions since Graph API doesn't support this
      throw new Error(
        `Converting to shared mailbox is not supported via Microsoft Graph API. ` +
        `Please use Exchange Online PowerShell:\n` +
        `Connect-ExchangeOnline\n` +
        `Set-Mailbox -Identity "${email}" -Type Shared\n` +
        `Or use Exchange Admin Center: https://admin.exchange.microsoft.com`
      );
    } catch (error) {
      logger.error('Error with shared mailbox conversion:', error);
      throw error;
    }
  }

  // Teams Management Methods
  async getUserTeams(userId) {
    try {
      // Get all teams (Microsoft 365 groups with Teams enabled)
      const response = await this.makeRequest(`/users/${userId}/joinedTeams?$select=id,displayName,description`);
      const allTeams = response.value || [];
      
      // Filter out teams that are backed by dynamic groups
      // Need to check each team's underlying group for dynamic membership
      const nonDynamicTeams = [];
      for (const team of allTeams) {
        try {
          // Get group details to check if it's dynamic
          const groupInfo = await this.makeRequest(`/groups/${team.id}?$select=id,displayName,groupTypes`);
          const groupTypes = groupInfo.groupTypes || [];
          
          // Only include teams that are NOT dynamic groups
          if (!groupTypes.includes('DynamicMembership')) {
            nonDynamicTeams.push(team);
          }
        } catch (error) {
          logger.warn(`Could not check dynamic status for team ${team.displayName}:`, error);
          // If we can't check, include it (better to try and fail than skip)
          nonDynamicTeams.push(team);
        }
      }
      
      return {
        value: nonDynamicTeams,
        total: nonDynamicTeams.length
      };
    } catch (error) {
      logger.error('Error fetching user teams:', error);
      throw error;
    }
  }

  async removeUserFromTeam(teamId, userId) {
    return this.makeRequest(`/groups/${teamId}/members/${userId}/$ref`, {
      method: 'DELETE',
    });
  }

  // Enterprise Application Methods
  async getUserAppRoleAssignments(userId) {
    try {
      const result = await this.makeRequest(`/users/${userId}/appRoleAssignments`);
      
      if (!result || !result.value) {
        return { value: [], total: 0 };
      }

      // Fetch service principal details for each assignment to get app names
      const enrichedAssignments = await Promise.all(
        result.value.map(async (assignment) => {
          try {
            const servicePrincipal = await this.makeRequest(
              `/servicePrincipals/${assignment.resourceId}?$select=id,displayName,appId`
            );
            return {
              ...assignment,
              appDisplayName: servicePrincipal.displayName,
              appId: servicePrincipal.appId
            };
          } catch (error) {
            logger.warn(`Could not fetch details for service principal ${assignment.resourceId}:`, error);
            return {
              ...assignment,
              appDisplayName: 'Unknown Application',
              appId: assignment.resourceId
            };
          }
        })
      );

      return {
        value: enrichedAssignments,
        total: enrichedAssignments.length
      };
    } catch (error) {
      logger.error('Error fetching user app role assignments:', error);
      throw error;
    }
  }

  async removeUserFromEnterpriseApp(userId, appRoleAssignmentId) {
    return this.makeRequest(`/users/${userId}/appRoleAssignments/${appRoleAssignmentId}`, {
      method: 'DELETE',
    });
  }

  // Authentication Methods
  async getUserAuthenticationMethods(userId) {
    try {
      const methods = [];

      // Get phone authentication methods
      try {
        const phoneMethodsData = await this.makeRequest(`/users/${userId}/authentication/phoneMethods`);
        if (phoneMethodsData && phoneMethodsData.value) {
          methods.push(...phoneMethodsData.value.map(method => ({
            ...method,
            methodType: 'phone',
            displayName: `Phone: ${method.phoneNumber || 'Unknown'}`
          })));
        }
      } catch (error) {
        logger.warn('Could not fetch phone methods:', error);
      }

      // Get email authentication methods
      try {
        const emailMethodsData = await this.makeRequest(`/users/${userId}/authentication/emailMethods`);
        if (emailMethodsData && emailMethodsData.value) {
          methods.push(...emailMethodsData.value.map(method => ({
            ...method,
            methodType: 'email',
            displayName: `Email: ${method.emailAddress || 'Unknown'}`
          })));
        }
      } catch (error) {
        logger.warn('Could not fetch email methods:', error);
      }

      // Get FIDO2 authentication methods
      try {
        const fido2MethodsData = await this.makeRequest(`/users/${userId}/authentication/fido2Methods`);
        if (fido2MethodsData && fido2MethodsData.value) {
          methods.push(...fido2MethodsData.value.map(method => ({
            ...method,
            methodType: 'fido2',
            displayName: `FIDO2 Key: ${method.displayName || method.model || 'Unknown'}`
          })));
        }
      } catch (error) {
        logger.warn('Could not fetch FIDO2 methods:', error);
      }

      // Get Microsoft Authenticator methods
      try {
        const msAuthMethodsData = await this.makeRequest(`/users/${userId}/authentication/microsoftAuthenticatorMethods`);
        if (msAuthMethodsData && msAuthMethodsData.value) {
          methods.push(...msAuthMethodsData.value.map(method => ({
            ...method,
            methodType: 'microsoftAuthenticator',
            displayName: `Microsoft Authenticator: ${method.displayName || method.phoneAppVersion || 'Unknown'}`
          })));
        }
      } catch (error) {
        logger.warn('Could not fetch Microsoft Authenticator methods:', error);
      }

      // Get Windows Hello for Business methods
      try {
        const windowsHelloMethodsData = await this.makeRequest(`/users/${userId}/authentication/windowsHelloForBusinessMethods`);
        if (windowsHelloMethodsData && windowsHelloMethodsData.value) {
          methods.push(...windowsHelloMethodsData.value.map(method => ({
            ...method,
            methodType: 'windowsHelloForBusiness',
            displayName: `Windows Hello: ${method.displayName || 'Unknown'}`
          })));
        }
      } catch (error) {
        logger.warn('Could not fetch Windows Hello methods:', error);
      }

      return {
        value: methods,
        total: methods.length
      };
    } catch (error) {
      logger.error('Error fetching user authentication methods:', error);
      throw error;
    }
  }

  async removeAuthenticationMethod(userId, methodId, methodType) {
    let endpoint;
    
    switch (methodType) {
      case 'phone':
        endpoint = `/users/${userId}/authentication/phoneMethods/${methodId}`;
        break;
      case 'email':
        endpoint = `/users/${userId}/authentication/emailMethods/${methodId}`;
        break;
      case 'fido2':
        endpoint = `/users/${userId}/authentication/fido2Methods/${methodId}`;
        break;
      case 'microsoftAuthenticator':
        endpoint = `/users/${userId}/authentication/microsoftAuthenticatorMethods/${methodId}`;
        break;
      case 'windowsHelloForBusiness':
        endpoint = `/users/${userId}/authentication/windowsHelloForBusinessMethods/${methodId}`;
        break;
      default:
        throw new Error(`Unsupported authentication method type: ${methodType}`);
    }

    return this.makeRequest(endpoint, {
      method: 'DELETE',
    });
  }

  // Device Management Methods (Intune)
  async getUserDevices(userId) {
    return this.makeRequest(`/deviceManagement/managedDevices?$filter=userPrincipalName eq '${userId}'&$select=id,deviceName,manufacturer,model,operatingSystem,osVersion,complianceState,lastSyncDateTime`);
  }

  async retireDevice(deviceId) {
    return this.makeRequest(`/deviceManagement/managedDevices/${deviceId}/retire`, {
      method: 'POST',
    });
  }

  async wipeDevice(deviceId, keepEnrollmentData, keepUserData) {
    return this.makeRequest(`/deviceManagement/managedDevices/${deviceId}/wipe`, {
      method: 'POST',
      body: JSON.stringify({
        keepEnrollmentData: keepEnrollmentData || false,
        keepUserData: keepUserData || false,
      }),
    });
  }

  async syncDevice(deviceId) {
    return this.makeRequest(`/deviceManagement/managedDevices/${deviceId}/syncDevice`, {
      method: 'POST',
    });
  }

  async getUserInstalledApps(userId) {
    return this.makeRequest(`/deviceAppManagement/mobileApps?$filter=owner eq '${userId}'`);
  }

  async removeAppFromDevice(deviceId, appId) {
    return this.makeRequest(`/deviceAppManagement/mobileApps/${appId}/managedDevices/${deviceId}/$ref`, {
      method: 'DELETE',
    });
  }

  // SharePoint/OneDrive Methods
  async getUserOneDriveUsage(userId) {
    return this.makeRequest(`/users/${userId}/drive?$select=id,name,lastModifiedDateTime,quota`);
  }

  async getUserFiles(userId) {
    return this.makeRequest(`/users/${userId}/drive/root/children?$select=id,name,file,folder,size,lastModifiedDateTime`);
  }

  async transferFileOwnership(userId, newOwnerId, fileId) {
    return this.makeRequest(`/drives/${userId}/items/${fileId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        '@microsoft.graph.downloadUrl': '',
        'permissions': [{
          'grantedTo': {
            'user': {
              'displayName': newOwnerId
            }
          },
          'roles': ['owner']
        }]
      }),
    });
  }

  async backupUserData(userId) {
    // This would typically trigger a backup process
    // For now, we'll return a placeholder response
    return {
      success: true,
      message: 'Backup process initiated',
      backupId: `backup_${userId}_${Date.now()}`,
    };
  }

  // Directory Methods
  async getUserDirectReports(userId) {
    return this.makeRequest(`/users/${userId}/directReports?$select=displayName,userPrincipalName,jobTitle`);
  }

  async getUserManager(userId) {
    return this.makeRequest(`/users/${userId}/manager?$select=displayName,userPrincipalName,jobTitle`);
  }

  async assignManager(userId, managerId) {
    return this.makeRequest(`/users/${userId}/manager/$ref`, {
      method: 'PUT',
      body: JSON.stringify({
        "@odata.id": `https://graph.microsoft.com/v1.0/users/${managerId}`
      }),
    });
  }

  // License Management
  /**
   * Get available licenses (subscribed SKUs) for the organization
   * @returns {Promise} Available licenses with details
   */
  async getAvailableLicenses() {
    if (isDemoMode()) {
      return {
        value: [
          {
            skuId: 'demo-sku-1',
            skuPartNumber: 'ENTERPRISEPACK',
            servicePlans: [],
            prepaidUnits: { enabled: 10 },
            consumedUnits: 5,
          },
        ],
      };
    }

    return this.makeRequest('/subscribedSkus');
  }

  /**
   * Get user license details
   * @param {string} userId - User ID
   * @returns {Promise} License details
   */
  async getUserLicenses(userId) {
    return this.makeRequest(`/users/${userId}/licenseDetails`);
  }

  /**
   * Assign license to user
   * Per Microsoft Graph API documentation: POST /users/{id}/assignLicense
   * @param {string} userId - User ID
   * @param {string} skuId - SKU ID to assign
   * @param {Array<string>} removeLicenses - Array of SKU IDs to remove
   * @returns {Promise} Response
   */
  async assignLicense(userId, skuId, removeLicenses = []) {
    return this.makeRequest(`/users/${userId}/assignLicense`, {
      method: 'POST',
      body: JSON.stringify({
        addLicenses: [{ skuId }],
        removeLicenses,
      }),
    });
  }

  /**
   * Assign multiple licenses to user (for onboarding)
   * Per Microsoft Graph API documentation: POST /users/{id}/assignLicense
   * @param {string} userId - User ID
   * @param {Array<string>} skuIds - Array of SKU IDs to assign
   * @returns {Promise} Response
   */
  async assignLicenses(userId, skuIds) {
    if (!skuIds || skuIds.length === 0) {
      return Promise.resolve({ success: true, assignedCount: 0 });
    }

    // Convert SKU ID array to addLicenses format
    const addLicenses = skuIds.map(skuId => ({ skuId }));

    return this.makeRequest(`/users/${userId}/assignLicense`, {
      method: 'POST',
      body: JSON.stringify({
        addLicenses,
        removeLicenses: [],
      }),
    });
  }

  /**
   * Remove a single license from user
   * Per Microsoft Graph API documentation: POST /users/{id}/assignLicense
   * @param {string} userId - User ID
   * @param {string} skuId - SKU ID to remove
   * @returns {Promise} Response
   */
  async removeLicense(userId, skuId) {
    return this.makeRequest(`/users/${userId}/assignLicense`, {
      method: 'POST',
      body: JSON.stringify({
        addLicenses: [],
        removeLicenses: [skuId],
      }),
    });
  }

  /**
   * Remove all licenses from user
   * Per Microsoft Graph API documentation: POST /users/{id}/assignLicense
   * Uses correct format: removeLicenses is an array of SKU ID strings
   * @param {string} userId - User ID
   * @returns {Promise} Response with removedCount
   */
  async removeAllLicenses(userId) {
    try {
      // Get current licenses - need to get assignedLicenses from user object, not licenseDetails
      const user = await this.makeRequest(`/users/${userId}?$select=id,assignedLicenses`);
      const assignedLicenses = user.assignedLicenses || [];
      
      if (assignedLicenses.length === 0) {
        return { success: true, removedCount: 0 };
      }

      // Extract SKU IDs - removeLicenses expects array of strings
      const skuIds = assignedLicenses.map(license => license.skuId);

      // Remove all licenses in a single API call
      await this.makeRequest(`/users/${userId}/assignLicense`, {
        method: 'POST',
        body: JSON.stringify({
          addLicenses: [],
          removeLicenses: skuIds,
        }),
      });

      return { success: true, removedCount: skuIds.length };
    } catch (error) {
      logger.error('Error removing all licenses:', error);
      throw new Error(`Failed to remove licenses: ${error.message}`);
    }
  }

  /**
   * Revoke all user sign-in sessions
   * Per Microsoft Graph API documentation: POST /users/{id}/revokeSignInSessions
   * This invalidates all refresh tokens and session tokens for the user
   * Essential for security during offboarding
   * @param {string} userId - User ID
   * @returns {Promise} Response
   */
  async revokeUserSessions(userId) {
    // In demo mode, just return success
    if (isDemoMode()) {
      return Promise.resolve({ success: true, value: true });
    }

    return this.makeRequest(`/users/${userId}/revokeSignInSessions`, {
      method: 'POST',
    });
  }

  // Audit and Reporting
  async getUserSignInActivity(userId) {
    return this.makeRequest(`/users/${userId}/signInActivity`);
  }

  async getAuditLogs(startDate, endDate) {
    const filter = `activityDateTime ge ${startDate} and activityDateTime le ${endDate}`;
    return this.makeRequest(`/auditLogs/directoryAudits?$filter=${filter}&$top=50`);
  }

  /**
   * Get user sign-in logs (requires Azure AD Premium P1/P2)
   * @param {string} userId - User ID or userPrincipalName
   * @param {number} days - Number of days to look back (default 7)
   * @returns {Promise} Sign-in logs
   */
  async getUserSignInLogs(userId, days = 7) {
    if (isDemoMode()) {
      // Return mock sign-in data
      const now = new Date();
      return {
        value: [
          {
            id: '1',
            createdDateTime: new Date(now - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
            userPrincipalName: 'demo.user@demo.com',
            userId: userId,
            appDisplayName: 'Microsoft Teams',
            appId: 'cc15fd57-2c6c-4117-a88c-83b1d56b4bbe',
            ipAddress: '192.168.1.100',
            clientAppUsed: 'Browser',
            deviceDetail: {
              displayName: 'Windows 10',
              operatingSystem: 'Windows 10',
              browser: 'Edge',
              isCompliant: true,
              isManaged: true
            },
            location: {
              city: 'Seattle',
              state: 'Washington',
              countryOrRegion: 'US',
              geoCoordinates: { latitude: 47.6062, longitude: -122.3321 }
            },
            status: { errorCode: 0, failureReason: null, additionalDetails: 'Success' },
            authenticationDetails: [
              { authenticationMethod: 'Password', succeeded: true }
            ],
            authenticationRequirement: 'singleFactorAuthentication'
          },
          {
            id: '2',
            createdDateTime: new Date(now - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
            userPrincipalName: 'demo.user@demo.com',
            userId: userId,
            appDisplayName: 'Microsoft 365 Portal',
            appId: '4765445b-32c6-49b0-83e6-1d93765276ca',
            ipAddress: '10.0.0.50',
            clientAppUsed: 'Mobile Apps and Desktop clients',
            deviceDetail: {
              displayName: 'iPhone 14',
              operatingSystem: 'iOS 17',
              browser: 'Mobile Safari',
              isCompliant: true,
              isManaged: true
            },
            location: {
              city: 'Redmond',
              state: 'Washington',
              countryOrRegion: 'US',
              geoCoordinates: { latitude: 47.6740, longitude: -122.1215 }
            },
            status: { errorCode: 0, failureReason: null, additionalDetails: 'Success' },
            authenticationDetails: [
              { authenticationMethod: 'Previously satisfied', succeeded: true }
            ],
            authenticationRequirement: 'singleFactorAuthentication'
          },
          {
            id: '3',
            createdDateTime: new Date(now - 1000 * 60 * 60 * 48).toISOString(), // 2 days ago
            userPrincipalName: 'demo.user@demo.com',
            userId: userId,
            appDisplayName: 'SharePoint Online',
            appId: '00000003-0000-0ff1-ce00-000000000000',
            ipAddress: '192.168.1.100',
            clientAppUsed: 'Browser',
            deviceDetail: {
              displayName: 'Windows 10',
              operatingSystem: 'Windows 10',
              browser: 'Chrome',
              isCompliant: true,
              isManaged: false
            },
            location: {
              city: 'Seattle',
              state: 'Washington',
              countryOrRegion: 'US',
              geoCoordinates: { latitude: 47.6062, longitude: -122.3321 }
            },
            status: { errorCode: 0, failureReason: null, additionalDetails: 'Success' },
            authenticationDetails: [
              { authenticationMethod: 'Password', succeeded: true },
              { authenticationMethod: 'SMS', succeeded: true }
            ],
            authenticationRequirement: 'multiFactorAuthentication'
          }
        ]
      };
    }

    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    const filter = `userId eq '${userId}' and createdDateTime ge ${startDate}`;
    return this.makeRequest(
      `/auditLogs/signIns?$filter=${filter}&$top=50&$orderby=createdDateTime desc`
    );
  }

  /**
   * Get user's registered authentication methods
   * @param {string} userId - User ID
   * @returns {Promise} Authentication methods
   */
  async getUserAuthenticationMethods(userId) {
    if (isDemoMode()) {
      return {
        value: [
          {
            '@odata.type': '#microsoft.graph.passwordAuthenticationMethod',
            id: 'password-1',
            password: null,
            createdDateTime: '2024-01-01T00:00:00Z'
          },
          {
            '@odata.type': '#microsoft.graph.phoneAuthenticationMethod',
            id: 'phone-1',
            phoneNumber: '+1 555-0100',
            phoneType: 'mobile',
            smsSignInState: 'ready'
          },
          {
            '@odata.type': '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod',
            id: 'authenticator-1',
            displayName: 'iPhone 14',
            deviceTag: 'iOS',
            phoneAppVersion: '6.7.0',
            createdDateTime: '2024-03-15T00:00:00Z'
          },
          {
            '@odata.type': '#microsoft.graph.emailAuthenticationMethod',
            id: 'email-1',
            emailAddress: 'backup@example.com'
          }
        ]
      };
    }

    return this.makeRequest(`/users/${userId}/authentication/methods`);
  }

  /**
   * Reset user password
   * @param {string} userId - User ID
   * @param {string} newPassword - New password (optional, will be auto-generated if not provided)
   * @returns {Promise} Password reset response
   */
  async resetUserPassword(userId, newPassword = null) {
    if (isDemoMode()) {
      return {
        '@odata.context': 'https://graph.microsoft.com/v1.0/$metadata#users/$entity',
        newPassword: newPassword || 'TempPass123!@#',
        resetSuccess: true
      };
    }

    // First get the password method ID
    const methods = await this.getUserAuthenticationMethods(userId);
    const passwordMethod = methods.value?.find(m => 
      m['@odata.type'] === '#microsoft.graph.passwordAuthenticationMethod'
    );

    if (!passwordMethod) {
      throw new Error('Password authentication method not found');
    }

    const body = newPassword ? { newPassword } : {};

    return this.makeRequest(
      `/users/${userId}/authentication/passwordMethods/${passwordMethod.id}/resetPassword`,
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );
  }

  /**
   * Delete user authentication method
   * @param {string} userId - User ID
   * @param {string} methodId - Authentication method ID
   * @param {string} methodType - Type of method (phone, email, fido2, etc.)
   * @returns {Promise} Deletion response
   */
  async deleteUserAuthenticationMethod(userId, methodId, methodType) {
    if (isDemoMode()) {
      return { success: true };
    }

    const methodTypeMap = {
      'phone': 'phoneMethods',
      'email': 'emailMethods',
      'fido2': 'fido2Methods',
      'microsoftAuthenticator': 'microsoftAuthenticatorMethods',
      'temporaryAccessPass': 'temporaryAccessPassMethods',
      'softwareOath': 'softwareOathMethods'
    };

    const endpoint = methodTypeMap[methodType] || 'methods';

    return this.makeRequest(
      `/users/${userId}/authentication/${endpoint}/${methodId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Get user's Teams presence
   * @param {string} userId - User ID
   * @returns {Promise} Presence information
   */
  async getUserPresence(userId) {
    if (isDemoMode()) {
      const presenceStates = ['Available', 'Busy', 'Away', 'BeRightBack', 'DoNotDisturb'];
      const randomState = presenceStates[Math.floor(Math.random() * presenceStates.length)];
      return {
        id: userId,
        availability: randomState,
        activity: randomState === 'Busy' ? 'InACall' : 'Available'
      };
    }

    return this.makeRequest(`/users/${userId}/presence`);
  }

  /**
   * Get user's registered devices
   * @param {string} userId - User ID
   * @returns {Promise} Registered devices
   */
  async getUserRegisteredDevices(userId) {
    if (isDemoMode()) {
      return {
        value: [
          {
            id: 'device-1',
            displayName: 'DESKTOP-WIN10',
            operatingSystem: 'Windows',
            operatingSystemVersion: '10.0.19044',
            trustType: 'AzureAd',
            isCompliant: true,
            isManaged: true,
            approximateLastSignInDateTime: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
          },
          {
            id: 'device-2',
            displayName: 'iPhone 14',
            operatingSystem: 'iOS',
            operatingSystemVersion: '17.0',
            trustType: 'AzureAd',
            isCompliant: true,
            isManaged: true,
            approximateLastSignInDateTime: new Date(Date.now() - 1000 * 60 * 30).toISOString()
          }
        ]
      };
    }

    return this.makeRequest(`/users/${userId}/registeredDevices`);
  }

  /**
   * Get user's manager
   * @param {string} userId - User ID
   * @returns {Promise} Manager information
   */
  async getUserManager(userId) {
    if (isDemoMode()) {
      return {
        id: 'manager-1',
        displayName: 'Sarah Johnson',
        mail: 'sarah.johnson@demo.com',
        jobTitle: 'Engineering Manager',
        userPrincipalName: 'sarah.johnson@demo.com'
      };
    }

    return this.makeRequest(`/users/${userId}/manager`);
  }

  /**
   * Disable user account
   * @param {string} userId - User ID
   * @returns {Promise} Update response
   */
  async disableUserAccount(userId) {
    if (isDemoMode()) {
      return { success: true, accountEnabled: false };
    }

    return this.makeRequest(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ accountEnabled: false })
    });
  }

  /**
   * Enable user account
   * @param {string} userId - User ID
   * @returns {Promise} Update response
   */
  async enableUserAccount(userId) {
    if (isDemoMode()) {
      return { success: true, accountEnabled: true };
    }

    return this.makeRequest(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ accountEnabled: true })
    });
  }
}

// Create a singleton instance
export const graphService = new GraphService();

export default GraphService;
